import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Loader2, CheckCircle2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SectionHeader } from '@/components/SectionHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportCenterPage() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [channel, setChannel] = useState('all');
  const [dataType, setDataType] = useState('daily-marketing');
  const [month, setMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });
  const [format, setFormat] = useState('excel');

  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const res = await axios.get(`${API}/ib/list`, h);
        setChannels(Array.isArray(res.data) ? res.data : []);
      } catch {}
      finally { setLoading(false); }
    };
    fetchChannels();
  }, []);

  const months = [];
  for (let y = 2026; y <= new Date().getFullYear(); y++) {
    for (let m = 1; m <= (y === new Date().getFullYear() ? new Date().getMonth() + 1 : 12); m++) {
      months.push(`${y}-${String(m).padStart(2, '0')}`);
    }
  }

  const handleExport = async () => {
    setExporting(true);
    try {
      const { exportToExcel, exportToPDF, exportMultiSheetExcel } = await import('@/utils/export');
      
      const targetChannels = channel === 'all' ? channels : channels.filter(c => c.id === channel);
      
      if (dataType.startsWith('daily-')) {
        const tab = dataType.replace('daily-', '');
        const allData = [];
        
        for (const ch of targetChannels) {
          if (tab === 'operation') {
            const res = await axios.get(`${API}/operation-data/${ch.id}`, h);
            const entries = res.data?.data || [];
            entries.forEach(day => {
              (day.trades || []).forEach(t => {
                allData.push({ Channel: ch.name, Date: day.date, Pair: t.pair, Position: t.position, Status: t.status, Pips: t.pips, Channel_Type: t.channel || 'both', Signal: t.signal_generated || '' });
              });
            });
          } else {
            const res = await axios.get(`${API}/historical/2026/daily-data/${ch.id}?month=${month}`, h);
            const entries = tab === 'marketing' ? (res.data.marketing || []) : (res.data.sales || []);
            entries.forEach(e => allData.push({ Channel: ch.name, ...flattenEntry(e, tab) }));
          }
        }
        
        const fileName = `DailyData_${tab}_${month}_${channel === 'all' ? 'AllChannels' : targetChannels[0]?.name}`;
        if (format === 'excel') {
          exportToExcel(allData, tab.charAt(0).toUpperCase() + tab.slice(1), fileName);
        } else {
          const cols = Object.keys(allData[0] || {});
          const rows = allData.map(r => cols.map(c => r[c] ?? ''));
          exportToPDF(`Daily Data — ${tab}`, `${month} | ${channel === 'all' ? 'All Channels' : targetChannels[0]?.name}`, cols, rows, fileName, { landscape: true });
        }
        toast.success(`Exported ${allData.length} rows`);
        
      } else if (dataType === 'performance') {
        const [y, m] = month.split('-');
        const dateFrom = `${y}-${m}-01`;
        const dateTo = `${y}-${m}-${new Date(parseInt(y), parseInt(m), 0).getDate()}`;
        const res = await axios.get(`${API}/performance/overall`, { ...h, params: { date_from: dateFrom, date_to: dateTo } });
        const ibPerf = (res.data.ib_performance || []).map(ib => ({
          'IB Name': ib.ib_name, 'Commission (RM)': Math.round(ib.commission || 0), 'Traded Lot': ib.traded_lot || 0,
          'Active Client': ib.active_client || 0, 'Deposit ($)': Math.round(ib.deposit || 0), 'Leads': ib.leads || 0, 'Closed': ib.closed || 0
        }));
        const fileName = `Performance_${month}`;
        if (format === 'excel') { exportToExcel(ibPerf, 'Performance', fileName); }
        else {
          const cols = Object.keys(ibPerf[0] || {});
          const rows = ibPerf.map(r => cols.map(c => r[c] ?? ''));
          exportToPDF('Overall Performance', month, cols, rows, fileName, { landscape: true });
        }
        toast.success(`Exported performance data`);
        
      } else if (dataType === 'ib-list') {
        const data = channels.map(ch => ({
          'Channel': ch.name, 'Status': ch.status || 'active', 'Health Score': ch.health_score || 0,
          'Commission (RM)': ch.total_commission || 0, 'Traded Lot': ch.total_traded_lot || 0,
          'Active Client': ch.total_active_client || 0, 'Leads': ch.total_leads || 0, 'Closed': ch.total_closed || 0
        }));
        if (format === 'excel') { exportToExcel(data, 'IB List', `IB_List_${month}`); }
        else {
          const cols = Object.keys(data[0] || {});
          const rows = data.map(r => cols.map(c => r[c] ?? ''));
          exportToPDF('Introducing Brokers List', month, cols, rows, `IB_List_${month}`, { landscape: true });
        }
        toast.success(`Exported ${data.length} IBs`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const flattenEntry = (e, tab) => {
    const row = { Date: e.date };
    if (tab === 'marketing') {
      if (e.spend) Object.entries(e.spend).forEach(([k,v]) => { row[`Spend ${k.toUpperCase()}`] = v || 0; });
      row['Leads'] = e.leads || 0; row['LPV'] = e.lpv || 0; row['CTA'] = e.cta || 0;
      row['Subs TG'] = e.subsTG || 0; row['Subs Bot'] = e.subsBot || 0;
    } else {
      row['Total Lead'] = e.totalLead || e.total_lead || 0;
      row['New Closed'] = e.newClosed || e.closed || 0;
      row['Returning Closed'] = e.vipClosed || 0;
      for (const bk of ['brokerA','brokerB','brokerC','brokerD','brokerE']) {
        if (e[bk]?.active || e[bk]?.lots) { row[`${bk} Active`] = e[bk].active || 0; row[`${bk} Lots`] = e[bk].lots || 0; row[`${bk} Comm`] = e[bk].commission || 0; }
      }
      if (e.brokers) Object.entries(e.brokers).forEach(([k,v]) => { if (v?.active || v?.lots) { row[`${k} Active`] = v.active || 0; row[`${k} Lots`] = v.lots || 0; row[`${k} Comm`] = v.commission || v.commission_usd || 0; } });
    }
    return row;
  };

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader icon={Download} title="Export Center" subtitle="Download data dalam format Excel atau PDF" />

        <div className="max-w-2xl mx-auto mt-6">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6 space-y-5">
            
            <div>
              <label className="text-sm font-medium text-[hsl(var(--text-secondary))] mb-2 block">Data Type</label>
              <Select value={dataType} onValueChange={setDataType}>
                <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="daily-marketing">Daily Data — Marketing</SelectItem>
                  <SelectItem value="daily-sales">Daily Data — Sales</SelectItem>
                  <SelectItem value="daily-operation">Daily Data — Operation</SelectItem>
                  <SelectItem value="performance">Overall Performance</SelectItem>
                  <SelectItem value="ib-list">IB List (All Channels)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(dataType.startsWith('daily-') || dataType === 'performance') && (
              <div>
                <label className="text-sm font-medium text-[hsl(var(--text-secondary))] mb-2 block">Channel</label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    <SelectItem value="all">All Channels</SelectItem>
                    {channels.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[hsl(var(--text-secondary))] mb-2 block">Month</label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    {months.map(m => <SelectItem key={m} value={m}>{new Date(m + '-01').toLocaleDateString('en-MY', { year: 'numeric', month: 'long' })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[hsl(var(--text-secondary))] mb-2 block">Format</label>
                <div className="flex gap-2">
                  <button onClick={() => setFormat('excel')} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${format === 'excel' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))]'}`}>
                    <FileSpreadsheet size={18} /> Excel
                  </button>
                  <button onClick={() => setFormat('pdf')} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${format === 'pdf' ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))]'}`}>
                    <FileText size={18} /> PDF
                  </button>
                </div>
              </div>
            </div>

            <Button onClick={handleExport} disabled={exporting} className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] text-white h-12 text-base">
              {exporting ? <><Loader2 size={18} className="mr-2 animate-spin" /> Exporting...</> : <><Download size={18} className="mr-2" /> Export Now</>}
            </Button>
          </div>
        </div>
      </div>
    </ResponsiveLayout>
  );
}
